import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, CheckCircle2, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2)    { setErr("Passwords don't match.");                  return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white rounded-3xl p-7 shadow-soft text-center">
          <h1 className="text-xl font-bold text-brand-ink">Invalid reset link</h1>
          <p className="text-sm text-brand-muted mt-2">This reset link is missing or malformed.</p>
          <Link to="/forgot-password" className="mt-5 inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
      <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand-muted mb-6 hover:text-brand-ink">
        <ArrowLeft size={14} /> Back to sign in
      </Link>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-7 shadow-soft">
        {!done ? (
          <>
            <div className="w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4">
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-bold text-brand-ink">Choose a new password</h1>
            <p className="text-sm text-brand-muted mt-2 mb-5">Pick something you'll remember. Minimum 8 characters.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <input type={showPw ? "text" : "password"} required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8 chars)" data-testid="reset-pw-new"
                  className="w-full h-12 pl-4 pr-12 rounded-2xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-teal/30 text-sm" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="reset-pw-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full inline-flex items-center justify-center text-brand-muted hover:text-brand-ink hover:bg-brand-linen transition-colors">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <input type={showPw ? "text" : "password"} required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" data-testid="reset-pw-confirm"
                className="w-full h-12 px-4 rounded-2xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-teal/30 text-sm" />
              {err && <p className="text-sm text-red-600" data-testid="reset-pw-error">{err}</p>}
              <button type="submit" disabled={busy} data-testid="reset-pw-submit"
                className="w-full h-12 rounded-2xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90 disabled:opacity-60 transition-all">
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="reset-pw-done">
            <div className="w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4">
              <CheckCircle2 size={20} />
            </div>
            <h1 className="text-2xl font-bold text-brand-ink">Password updated</h1>
            <p className="text-sm text-brand-muted mt-2">Taking you back to sign in…</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
